import { GpmPayWebhookSignatureError } from '../core/errors.js';
import {
  SIGNATURE_HEADER,
  constructWebhookEvent,
  type GpmPayWebhookEvent,
} from './verify.js';

export interface NextVerifyOptions {
  secret: string;
  toleranceSeconds?: number;
  /** Defaults to `X-GPMPay-Signature`. */
  headerName?: string;
}

/**
 * Verify a webhook in a Next.js App Router route handler.
 *
 * `await request.text()` yields the exact bytes GPM Pay sent, so no special
 * body-parser configuration is needed.
 *
 * @throws {GpmPayWebhookSignatureError}
 */
export async function verifyNextRequest(
  request: Request,
  options: NextVerifyOptions,
): Promise<GpmPayWebhookEvent> {
  const { secret, toleranceSeconds, headerName = SIGNATURE_HEADER } = options;

  const rawBody = await request.text();
  const headers: Record<string, string> = {};
  request.headers.forEach((value, key) => {
    headers[key] = value;
  });

  return constructWebhookEvent({
    rawBody,
    signature: request.headers.get(headerName) ?? '',
    secret,
    headers,
    ...(toleranceSeconds === undefined ? {} : { toleranceSeconds }),
  });
}

/**
 * A ready-made App Router POST handler.
 *
 * @example
 * // app/api/webhooks/gpmpay/route.ts
 * import { createNextWebhookHandler } from '@gpmpay/sdk/webhooks';
 *
 * export const POST = createNextWebhookHandler({
 *   secret: process.env.GPMPAY_WEBHOOK_SECRET!,
 *   onEvent: async (event) => {
 *     if (event.payload.transferType !== 'in') return;
 *     const code = /DH(\d+)/.exec(event.payload.content)?.[0];
 *     if (code) await fulfil(code, event.payload.transferAmount);
 *   },
 * });
 */
export function createNextWebhookHandler(
  options: NextVerifyOptions & {
    onEvent: (event: GpmPayWebhookEvent) => void | Promise<void>;
  },
): (request: Request) => Promise<Response> {
  const { onEvent, ...verifyOptions } = options;

  return async function POST(request: Request): Promise<Response> {
    let event: GpmPayWebhookEvent;
    try {
      event = await verifyNextRequest(request, verifyOptions);
    } catch (error) {
      if (error instanceof GpmPayWebhookSignatureError) {
        return Response.json(
          { error: error.message, reason: error.reason },
          { status: 401 },
        );
      }
      throw error;
    }

    await onEvent(event);
    return Response.json({ received: true });
  };
}

/**
 * Read a raw body from a stream, for the Pages Router.
 *
 * Requires disabling the built-in parser:
 * ```ts
 * export const config = { api: { bodyParser: false } };
 * ```
 *
 * @param maxBytes Guard against unbounded bodies. Default 1 MiB.
 */
export async function readRawBody(
  stream: AsyncIterable<Uint8Array>,
  maxBytes = 1024 * 1024,
): Promise<Buffer> {
  const chunks: Buffer[] = [];
  let total = 0;

  for await (const chunk of stream) {
    const buf = Buffer.from(chunk);
    total += buf.length;
    if (total > maxBytes) {
      throw new Error(
        `Webhook body exceeded ${String(maxBytes)} bytes; refusing to buffer more.`,
      );
    }
    chunks.push(buf);
  }

  return Buffer.concat(chunks);
}
